import React, { useCallback, useEffect, useState } from 'react';
import api, { API_BASE } from '../../utils/api';
import { prepareFileForUpload } from '../../utils/imageUploadCompress';
import { downloadAuthenticatedUrl } from '../../utils/authenticatedBlobDownload';
import { usePortal } from '../../context/PortalContext';
import { useAuth } from '../../context/AuthContext';
import PortalPageShell, { PortalEmpty, PortalLoading } from './PortalPageShell';
import ChildSelector from './ChildSelector';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Download, Trash2, Upload } from 'lucide-react';

const PROGRESS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

function statusLabel(a) {
  if (a.is_reviewed) return 'Reviewed';
  const ps = a.progress_status || 'pending';
  if (ps === 'completed') return 'Completed';
  if (ps === 'in_progress') return 'In Progress';
  return 'Pending';
}

function statusVariant(a) {
  if (a.is_reviewed) return 'default';
  if ((a.progress_status || '') === 'completed') return 'secondary';
  return 'outline';
}

export default function PortalHomework() {
  const { user } = useAuth();
  const { activeStudentId, portalMode } = usePortal();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [text, setText] = useState({});

  const isStudent = user?.role_slug === 'student';
  const studentId = isStudent ? null : activeStudentId;

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = studentId ? { student_id: studentId } : {};
      const res = await api.get('/assignments', { params });
      setItems(res.data?.data ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleProgressChange = async (assignmentId, progressStatus) => {
    setSubmitting(assignmentId);
    try {
      await api.patch(`/assignments/${assignmentId}/progress`, { progress_status: progressStatus });
      toast({ title: 'Updated', description: `Status set to ${progressStatus.replace('_', ' ')}.` });
      await loadItems();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Could not update status', variant: 'destructive' });
    } finally {
      setSubmitting(null);
    }
  };

  const handleSubmit = async (assignmentId) => {
    setSubmitting(assignmentId);
    try {
      await api.post(`/assignments/${assignmentId}/submit`, {
        submission_text: text[assignmentId] || '',
        progress_status: 'in_progress',
      });
      toast({ title: 'Saved', description: 'Your notes were saved.' });
      await loadItems();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Could not save', variant: 'destructive' });
    } finally {
      setSubmitting(null);
    }
  };

  const handleFileUpload = async (assignmentId, file) => {
    if (!file) return;
    setUploading(assignmentId);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('file', prepared);
      await api.post(`/assignments/${assignmentId}/files`, formData);
      toast({ title: 'Uploaded', description: prepared.name || file.name });
      await loadItems();
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.message || e.message || 'Could not upload file', variant: 'destructive' });
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await api.delete(`/assignments/files/${fileId}`);
      toast({ title: 'Removed', description: 'File deleted.' });
      await loadItems();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Could not delete file', variant: 'destructive' });
    }
  };

  const openFile = async (fileId, fileName) => {
    try {
      await downloadAuthenticatedUrl(
        `${API_BASE}/api/assignments/files/${fileId}/download`,
        fileName || 'file',
      );
    } catch {
      toast({ title: 'Error', description: 'Could not open file', variant: 'destructive' });
    }
  };

  const openAssignmentAttachment = async (assignmentId, fileName) => {
    try {
      const query = studentId ? `?student_id=${studentId}` : '';
      await downloadAuthenticatedUrl(
        `${API_BASE}/api/assignments/${assignmentId}/attachment${query}`,
        fileName || 'question-sheet',
      );
    } catch {
      toast({ title: 'Error', description: 'Could not open file', variant: 'destructive' });
    }
  };

  return (
    <PortalPageShell
      title="Homework & Assignments"
      description={portalMode === 'parent' ? 'Assigned work and submission status for your child' : 'Your class assignments'}
      actions={portalMode === 'parent' ? <ChildSelector /> : null}
    >
      {loading ? <PortalLoading /> : null}
      {!loading && items.length === 0 ? <PortalEmpty message="No assignments right now." /> : null}
      <div className="space-y-3">
        {items.map((a) => {
          const locked = a.is_reviewed;
          const canEdit = isStudent && !locked;

          return (
            <Card key={a.id}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-base">{a.title}</CardTitle>
                  <Badge variant={statusVariant(a)}>{statusLabel(a)}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {a.subject_name || 'Subject'} · Due {a.due_date ? new Date(a.due_date).toLocaleString() : '—'}
                  {a.submission_marks != null ? ` · Marks: ${a.submission_marks}/${a.max_marks ?? 100}` : ''}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {a.description ? <p className="text-sm whitespace-pre-wrap">{a.description}</p> : null}
                {a.has_attachment ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => openAssignmentAttachment(a.id, a.attachment_original_name)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download question sheet
                  </Button>
                ) : null}
                {a.teacher_remark ? (
                  <p className="text-sm rounded-md bg-muted p-3">
                    <span className="font-medium">Teacher remark:</span> {a.teacher_remark}
                  </p>
                ) : null}

                {canEdit ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Progress</p>
                    <div className="flex flex-wrap gap-2">
                      {PROGRESS_OPTIONS.map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          size="sm"
                          variant={(a.progress_status || 'pending') === opt.value ? 'default' : 'outline'}
                          disabled={submitting === a.id}
                          onClick={() => handleProgressChange(a.id, opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : !isStudent && a.progress_status ? (
                  <p className="text-sm text-muted-foreground">Progress: {statusLabel(a)}</p>
                ) : null}

                {canEdit ? (
                  <>
                    <Textarea
                      placeholder="Your answer or notes..."
                      value={text[a.id] ?? a.submission_text ?? ''}
                      onChange={(e) => setText((prev) => ({ ...prev, [a.id]: e.target.value }))}
                    />
                    <Button size="sm" variant="outline" disabled={submitting === a.id} onClick={() => handleSubmit(a.id)}>
                      Save notes
                    </Button>
                  </>
                ) : a.submission_text ? (
                  <p className="text-sm rounded-md border p-3 whitespace-pre-wrap">{a.submission_text}</p>
                ) : null}

                {(a.files?.length > 0 || canEdit) ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Files</p>
                    {a.files?.map((f) => (
                      <div key={f.id} className="flex items-center justify-between gap-2 text-sm border rounded-md px-3 py-2">
                        <span className="truncate">{f.original_name}</span>
                        <div className="flex gap-1 shrink-0">
                          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => openFile(f.id, f.original_name)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          {canEdit ? (
                            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDeleteFile(f.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                    {canEdit ? (
                      <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-primary">
                        <Upload className="h-4 w-4" />
                        {uploading === a.id ? 'Uploading…' : 'Upload screenshot or file'}
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*,.pdf,.doc,.docx"
                          disabled={uploading === a.id}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(a.id, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </PortalPageShell>
  );
}
